/**
 * The board's Measure & estimate toolbar: a compact vertical strip on the
 * right edge (Excalidraw's own UI owns the top, the left properties panel and
 * the bottom corners). Every measuring action has a visible button with its
 * shortcut in the tooltip, plus a context card for the current selection
 * (a line → its length, Edit points, Measure this line, Assign, Curved/Sharp;
 * a drawing → its scale, Set scale, Printed scale, Lock; a fixture → its type
 * and quantity). Collapsible; the collapsed state and the first-time hint are
 * remembered per browser.
 */
import { useEffect, useRef, useState } from 'react';
import { SCALE_PRESETS, parseLength, type MeasureDisplay } from '../../shared/src/measure';
import { withKey, type CommandId } from '../../shared/src/keymap';
import { DISPLAY_SHORT, type DblClickSettings } from '../../shared/src/measureTools';
import type { SelectedImage, SelectedMeasure } from '../../shared/src/components/EstimateMeasure';
import { ScopeSelect } from '../../shared/src/components/ScopeSelect';
import type { ScopeChoices } from '../../shared/src/scopes';

const COLLAPSED_KEY = 'xb.measureBar.collapsed';
const HINT_KEY = 'xb.measureBar.hint';

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
function writeFlag(key: string, on: boolean) {
  try {
    localStorage.setItem(key, on ? '1' : '0');
  } catch {
    /* private window */
  }
}

export type MeasureMode = 'fixture' | 'linear' | 'area' | 'calibrate' | 'dimension' | 'mirror' | 'zone' | null;

export interface SelectedLineInfo {
  elementId: string;
  type: 'line' | 'arrow' | 'shape';
  curved: boolean;
  editing: boolean;
  elbowed: boolean;
  /** Counting an EACH type along this line: its type, one fixture's length, and the live count. */
  along?: { itemId: string; code: string; unitLength: number; unit: 'm' | 'ft'; length: number | null; count: number | null } | null;
}

/** An EACH type offered under "Count along the run", with its remembered fixture length. */
export interface CountOption {
  id: string;
  label: string;
  unitLength: number | null;
  unit: 'm' | 'ft';
}

export interface SelectedFixtureInfo {
  elementId: string;
  label: string;
  qty: number;
  measure: 'count' | 'linear' | 'area';
  /** This placement's own SCOPE (blank = inherit). */
  scope?: string;
}

export interface MeasureToolbarProps {
  mode: MeasureMode;
  panelOpen: boolean;
  display: MeasureDisplay;
  unitsEnabled: boolean;
  image: SelectedImage | null;
  measure: SelectedMeasure | null;
  line: SelectedLineInfo | null;
  fixture: SelectedFixtureInfo | null;
  dbl: DblClickSettings;
  onFixture(): void;
  onRun(): void;
  onArea(): void;
  onCalibrate(): void;
  onPreset(imageId: string, presetKey: string): void;
  onToggleLock(imageId: string): void;
  onRemoveScale(imageId: string): void;
  onUnits(): void;
  onTogglePanel(): void;
  onEditPoints(id: string): void;
  onMeasureLine(id: string): void;
  onAssign(): void;
  /** Items a selected line / shape can count as (runs: LF/M; shapes: SF/M2) and preset types to create. */
  assignOptions?: { items: { id: string; label: string }[]; counts?: CountOption[]; presets: { key: string; label: string }[] } | null;
  onAssignTo?(elementId: string, choice: { itemId?: string; presetKey?: string; along?: { unit_length: number; unit: 'm' | 'ft' } }): void;
  onToggleCurve(id: string): void;
  /** Dimension tool (⇧D). */
  onDimension?(): void;
  /** Mirror the selection across a two-click axis (hidden for read-only viewers). */
  onMirror?(): void;
  /** Draw a ZONE (freehand or click points); fixtures inside get that ZONE in the workbook. */
  onZone?(): void;
  /** The selected zone, if any. */
  zone?: { id: string; name: string; count: number; scope?: string; hidden?: boolean } | null;
  onRenameZone?(id: string, name: string): void;
  onFocusZone?(id: string): void;
  /** SCOPE list (workbook register when linked) for the zone / fixture cards. */
  scopeChoices?: ScopeChoices;
  onZoneScope?(id: string, scope: string): void;
  /** Hide a zone (set and forget: invisible, locked, still counts for ZONE). */
  onZoneHidden?(id: string, hidden: boolean): void;
  onFixtureScope?(elementId: string, scope: string): void;
  /** The selected dimension line, if any. */
  dimension?: { elementId: string; text: string; ends: 'bar' | 'arrow' } | null;
  onDimensionEnds?(id: string, ends: 'bar' | 'arrow'): void;
  onDelete?(id: string): void;
  onDbl(next: DblClickSettings): void;
  /** Re-size every fixture symbol to the drawing under it (one undo step). */
  onResizeFixtures?(): void;
}

function Btn(p: { icon: string; label: string; short: string; cmd?: CommandId; hint?: string; active?: boolean; disabled?: boolean; onClick(): void; testId: string }) {
  const title = p.cmd ? withKey(p.label, p.cmd) : p.hint ? `${p.label} — ${p.hint}` : p.label;
  return (
    <button
      type="button"
      className={`xb-mt-btn${p.active ? ' is-active' : ''}`}
      title={title}
      aria-label={p.label}
      aria-pressed={p.active ? true : undefined}
      disabled={p.disabled}
      onClick={p.onClick}
      data-testid={p.testId}
    >
      <span className="xb-mt-ico" aria-hidden>
        {p.icon}
      </span>
      <span className="xb-mt-lbl">{p.short}</span>
    </button>
  );
}

export function MeasureToolbar(p: MeasureToolbarProps) {
  const [collapsed, setCollapsed] = useState(() => readFlag(COLLAPSED_KEY));
  const [hint, setHint] = useState(() => !readFlag(HINT_KEY));
  const [presetOpen, setPresetOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const presetRef = useRef<HTMLSelectElement | null>(null);
  useEffect(() => {
    if (presetOpen) presetRef.current?.focus();
  }, [presetOpen]);
  const img = p.image;

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      writeFlag(COLLAPSED_KEY, !c);
      return !c;
    });
  };
  const dismissHint = () => {
    setHint(false);
    writeFlag(HINT_KEY, true);
  };

  return (
    <div className={`xb-mt${p.panelOpen ? ' is-shifted' : ''}${collapsed ? ' is-collapsed' : ''}`} data-testid="measure-toolbar" onPointerDown={(e) => e.stopPropagation()}>
      <div className="xb-mt-bar" role="toolbar" aria-label="Measure and estimate">
        <button type="button" className="xb-mt-head" onClick={toggleCollapsed} title={collapsed ? 'Show the measure & estimate tools' : 'Hide the measure & estimate tools'} data-testid="measure-toolbar-toggle">
          📏{collapsed ? '' : <span className="xb-mt-lbl">Measure</span>}
        </button>
        {!collapsed ? (
          <>
            <Btn icon="💡" label="Place fixture" cmd="estimate.fixture" active={p.mode === 'fixture'} onClick={p.onFixture} testId="mt-fixture" short="Fixture" />
            <Btn icon="〰" label="Measure run" cmd="estimate.linear" active={p.mode === 'linear'} onClick={p.onRun} testId="mt-run" short="Run" />
            <Btn icon="▱" label="Measure area" cmd="estimate.area" active={p.mode === 'area'} onClick={p.onArea} testId="mt-area" short="Area" />
            {p.onDimension ? <Btn icon="↔" label="Dimension" cmd="estimate.dimension" active={p.mode === 'dimension'} onClick={p.onDimension} testId="mt-dimension" short="Dim" /> : null}
            {p.onMirror ? <Btn icon="⇹" label="Mirror selection" cmd="estimate.mirror" active={p.mode === 'mirror'} onClick={p.onMirror} testId="mt-mirror" short="Mirror" /> : null}
            {p.onZone ? <Btn icon="⬚" label="Zone" cmd="estimate.zone" active={p.mode === 'zone'} onClick={p.onZone} testId="mt-zone" short="Zone" /> : null}
            <div className="xb-mt-sep" />
            <Btn icon="📏" label="Set scale" cmd="estimate.calibrate" active={p.mode === 'calibrate'} onClick={p.onCalibrate} testId="mt-calibrate" short="Scale" />
            <Btn
              icon="📐"
              label="Printed scale"
              hint={img ? (img.hasSheetSize ? 'pick the scale printed on the sheet' : 'needs the sheet size — calibrate instead') : 'select a drawing first'}
              disabled={!img || !img.hasSheetSize}
              active={presetOpen}
              onClick={() => setPresetOpen((v) => !v)}
              testId="mt-printed" short="Printed"
            />
            <Btn
              icon={img?.locked ? '🔓' : '🔒'}
              label={img?.locked ? 'Unlock drawing' : 'Lock drawing'}
              hint={img ? 'the scale stays with the drawing' : 'select a drawing first'}
              disabled={!img}
              onClick={() => img && p.onToggleLock(img.elementId)}
              testId="mt-lock" short={img?.locked ? 'Unlock' : 'Lock'}
            />
            <Btn icon="⇔" label={`Units: ${DISPLAY_SHORT[p.display]}`} hint="click to switch: feet-inches · decimal feet · metres" disabled={!p.unitsEnabled} onClick={p.onUnits} testId="mt-units" short={DISPLAY_SHORT[p.display]} />
            <div className="xb-mt-sep" />
            <Btn icon="🧾" label="Estimate panel" cmd="estimate.panel" active={p.panelOpen} onClick={p.onTogglePanel} testId="mt-panel" short="Estimate" />
            <Btn icon="⚙" label="Options" hint="fixture sizes, what double-clicking a line does" active={settingsOpen} onClick={() => setSettingsOpen((v) => !v)} testId="mt-settings" short="Options" />
          </>
        ) : null}
      </div>

      {/* Out of the way while calibrating or measuring: the step banner guides then, and the drawing needs the room. */}
      <div className="xb-mt-side" hidden={p.mode === 'calibrate' || p.mode === 'linear' || p.mode === 'area'}>
      {!collapsed && presetOpen && img?.hasSheetSize ? (
        <div className="xb-mt-pop" data-testid="mt-printed-pop">
          <label className="xb-mt-pop-h" htmlFor="xb-mt-preset">
            Printed scale of {img.sourceLabel ?? 'this drawing'}
          </label>
          <select
            id="xb-mt-preset"
            ref={presetRef}
            className="rl-select"
            defaultValue=""
            data-testid="mt-printed-select"
            onChange={(ev) => {
              if (!ev.target.value) return;
              p.onPreset(img.elementId, ev.target.value);
              setPresetOpen(false);
            }}
          >
            <option value="">Pick the scale printed on the sheet…</option>
            {(['Architectural', 'Engineering', 'Metric'] as const).map((g) => (
              <optgroup key={g} label={g}>
                {SCALE_PRESETS.filter((s) => s.group === g).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="xb-mt-note">Only right if the drawing was printed at this scale on its sheet. A scale bar on the drawing? Use Set scale and click its ends.</p>
        </div>
      ) : null}

      {!collapsed && settingsOpen ? (
        <div className="xb-mt-pop" data-testid="mt-settings-pop">
          {p.onResizeFixtures ? (
            <>
              <div className="xb-mt-pop-h">Fixture symbols</div>
              <button type="button" className="xb-btn" onClick={p.onResizeFixtures} data-testid="mt-resize-fixtures" title="Every symbol becomes the standard size for the drawing it sits on">
                Resize all to their drawings
              </button>
            </>
          ) : null}
          <div className="xb-mt-pop-h">Double-click on…</div>
          <label className="xb-mt-check">
            <input type="checkbox" checked={p.dbl.line} onChange={(e) => p.onDbl({ ...p.dbl, line: e.target.checked })} data-testid="mt-dbl-line" /> a line edits its points
          </label>
          <label className="xb-mt-check">
            <input type="checkbox" checked={p.dbl.arrow} onChange={(e) => p.onDbl({ ...p.dbl, arrow: e.target.checked })} data-testid="mt-dbl-arrow" /> an arrow edits its points
          </label>
          <p className="xb-mt-note">Measured runs always edit points. Otherwise double-click adds a text label (Excalidraw's default). ⌘/Ctrl + Enter edits points of any selected line.</p>
        </div>
      ) : null}

      {!collapsed ? <ContextCard {...p} /> : null}

      {!collapsed && hint ? (
        <div className="xb-mt-hint" role="note" data-testid="mt-hint">
          <strong>Measuring & estimating live here.</strong> Set a drawing's scale, measure runs and areas, place fixtures. Hover a button for its shortcut.
          <button type="button" className="xb-btn" onClick={dismissHint} data-testid="mt-hint-dismiss">
            Got it
          </button>
        </div>
      ) : null}
      </div>
    </div>
  );
}

function trimNum(x: number): string {
  return Number.isInteger(x) ? String(x) : String(Math.round(x * 1000) / 1000);
}

/**
 * "Assign to item…": runs (LF / M types), "Count along the run" (EACH types: asks for one fixture's length,
 * pre-filled from the type's remembered length), or a new type from the library.
 */
function AssignPicker(p: MeasureToolbarProps & { elementId: string; prompt: string; current?: CountOption | null }) {
  const o = p.assignOptions;
  const [pending, setPending] = useState<CountOption | null>(null);
  const [len, setLen] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => setPending(null), [p.elementId]);
  const counts = o?.counts ?? [];
  if (!o || !p.onAssignTo || !(o.items.length || counts.length || o.presets.length)) return null;
  const start = (c: CountOption) => {
    setPending(c);
    setLen(c.unitLength ? `${trimNum(c.unitLength)} ${c.unit}` : '');
    setErr('');
  };
  const confirm = () => {
    if (!pending) return;
    const v = parseLength(len, pending.unit);
    if (!v || !(v.value > 0)) {
      setErr(`Enter one fixture's length, e.g. 1.2 m or 4'`);
      return;
    }
    p.onAssignTo!(p.elementId, { itemId: pending.id, along: { unit_length: Math.round(v.value * 10000) / 10000, unit: v.unit } });
    setPending(null);
  };
  if (pending) {
    return (
      <div className="xb-mt-along" data-testid="mt-along-form">
        <label className="xb-mt-note" htmlFor="mt-along-length">
          One {pending.label} is
        </label>
        <input
          id="mt-along-length"
          className="rl-input"
          value={len}
          autoFocus
          placeholder="1.2 m"
          onChange={(e) => setLen(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') confirm();
            if (e.key === 'Escape') setPending(null);
            e.stopPropagation();
          }}
          data-testid="mt-along-length"
        />
        {err ? <div className="xb-mt-warn">{err}</div> : <div className="xb-mt-note">Counts as many as fit along the line, rounded up.</div>}
        <div className="xb-mt-row">
          <button type="button" className="xb-btn xb-btn-primary" onClick={confirm} data-testid="mt-along-count">
            Count
          </button>
          <button type="button" className="xb-btn" onClick={() => setPending(null)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }
  return (
    <>
      {p.current ? (
        <button type="button" className="xb-btn" onClick={() => start(p.current!)} data-testid="mt-along-change">
          Change fixture length…
        </button>
      ) : null}
      <select
        className="rl-select xb-mt-assign"
        value=""
        onChange={(ev) => {
          const v = ev.target.value;
          if (!v) return;
          if (v.startsWith('each:')) {
            const c = counts.find((x) => x.id === v.slice(5));
            if (c) start(c);
            return;
          }
          p.onAssignTo!(p.elementId, v.startsWith('preset:') ? { presetKey: v.slice(7) } : { itemId: v });
        }}
        aria-label={p.prompt}
        title="Count it as a run of a type, or count a type along it"
        data-testid="mt-assign"
      >
        <option value="">{p.prompt}</option>
        {o.items.length ? (
          <optgroup label="Runs">
            {o.items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.label}
              </option>
            ))}
          </optgroup>
        ) : null}
        {counts.length ? (
          <optgroup label="Count along the run">
            {counts.map((c) => (
              <option key={c.id} value={`each:${c.id}`}>
                {c.label}
                {c.unitLength ? ` (${trimNum(c.unitLength)} ${c.unit} each)` : ''}
              </option>
            ))}
          </optgroup>
        ) : null}
        {o.presets.length ? (
          <optgroup label="New type">
            {o.presets.map((x) => (
              <option key={x.key} value={`preset:${x.key}`}>
                + {x.label}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
    </>
  );
}

function ZoneCard({
  zone,
  onRename,
  onFocus,
  onDelete,
  scopes,
  onScope,
  onHidden,
}: {
  zone: { id: string; name: string; count: number; scope?: string; hidden?: boolean };
  onRename?(id: string, name: string): void;
  onFocus?(id: string): void;
  onDelete?(id: string): void;
  scopes?: ScopeChoices;
  onScope?(id: string, scope: string): void;
  onHidden?(id: string, hidden: boolean): void;
}) {
  const [name, setName] = useState(zone.name);
  useEffect(() => setName(zone.name), [zone.id, zone.name]);
  const save = () => {
    const n = name.trim().toUpperCase();
    if (n && n !== zone.name) onRename?.(zone.id, n);
  };
  return (
    <div className="xb-mt-card" data-testid="mt-zone-card">
      <div className="xb-mt-card-h">Zone</div>
      <input
        className="rl-input xb-mt-assign"
        value={name}
        onChange={(e) => setName(e.target.value.toUpperCase())}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
            (e.target as HTMLInputElement).blur();
          }
        }}
        aria-label="Zone name"
        data-testid="mt-zone-name"
      />
      <div className="xb-mt-value" data-testid="mt-zone-count">
        {zone.count} fixture{zone.count === 1 ? '' : 's'} inside
      </div>
      <p className="xb-mt-note">Fixtures inside go in this ZONE on the AREA/ZONE BOM. Drag its points to reshape it (double-click the outline).</p>
      {scopes && onScope ? (
        <label className="xb-mt-field">
          <span className="xb-mt-note">Scope of the fixtures inside</span>
          <ScopeSelect value={zone.scope ?? ''} onChange={(v) => onScope(zone.id, v)} choices={scopes} inheritLabel="type / frame" testId="mt-zone-scope" />
        </label>
      ) : null}
      {onHidden ? (
        <button type="button" className="xb-btn" onClick={() => onHidden(zone.id, !zone.hidden)} data-testid="mt-zone-hide" title="Hidden zones still count for ZONE; show them again from the Estimate panel's Zones list">
          {zone.hidden ? '◉ Show zone' : '◌ Hide zone'}
        </button>
      ) : null}
      {onFocus && zone.count ? (
        <button type="button" className="xb-btn" onClick={() => onFocus(zone.id)} data-testid="mt-zone-find">
          ⌖ Select its fixtures
        </button>
      ) : null}
      {onDelete ? (
        <button type="button" className="xb-btn" onClick={() => onDelete(zone.id)} data-testid="mt-zone-delete">
          Delete zone
        </button>
      ) : null}
    </div>
  );
}

function ContextCard(p: MeasureToolbarProps) {
  const { line, measure, image, fixture } = p;
  if (p.zone)
    return <ZoneCard zone={p.zone} onRename={p.onRenameZone} onFocus={p.onFocusZone} onDelete={p.onDelete} scopes={p.scopeChoices} onScope={p.onZoneScope} onHidden={p.onZoneHidden} />;
  const dim = p.dimension;
  if (dim) {
    return (
      <div className="xb-mt-card" data-testid="mt-dimension-card">
        <div className="xb-mt-card-h">Dimension</div>
        <div className={dim.text === 'no scale' ? 'xb-mt-note' : 'xb-mt-value'} data-testid="mt-dimension-length">
          {dim.text === 'no scale' ? (
            <>
              No scale under it —{' '}
              <button type="button" className="xb-mt-link" onClick={p.onCalibrate}>
                set a scale
              </button>
            </>
          ) : (
            dim.text
          )}
        </div>
        <p className="xb-mt-note">Not counted in the BOM. Drag an end to re-measure.</p>
        <div className="xb-mt-row">
          <button type="button" className={`xb-btn${dim.ends === 'bar' ? ' is-on' : ''}`} onClick={() => p.onDimensionEnds?.(dim.elementId, 'bar')} data-testid="mt-dim-bars" aria-pressed={dim.ends === 'bar'}>
            ⊢ Ticks
          </button>
          <button type="button" className={`xb-btn${dim.ends === 'arrow' ? ' is-on' : ''}`} onClick={() => p.onDimensionEnds?.(dim.elementId, 'arrow')} data-testid="mt-dim-arrows" aria-pressed={dim.ends === 'arrow'}>
            ↔ Arrows
          </button>
        </div>
        <AssignPicker {...p} elementId={dim.elementId} prompt="Convert to run…" />
        <button type="button" className="xb-btn" onClick={() => p.onDelete?.(dim.elementId)} data-testid="mt-dim-delete">
          Delete
        </button>
      </div>
    );
  }
  if (line) {
    return (
      <div className="xb-mt-card" data-testid="mt-line-card">
        <div className="xb-mt-card-h">{line.type === 'shape' ? 'Shape' : line.type === 'arrow' ? 'Arrow' : 'Line'}</div>
        {measure ? (
          measure.text ? (
            <div className="xb-mt-value" data-testid="mt-line-length">
              {measure.text}
              {measure.on ? <small> on {measure.on}</small> : null}
              {measure.spans ? <small className="xb-mt-warn"> · crosses two scaled drawings</small> : null}
            </div>
          ) : (
            <div className="xb-mt-note" data-testid="mt-line-length">
              No scale under this {measure.kind === 'area' ? 'shape' : 'line'} —{' '}
              <button type="button" className="xb-mt-link" onClick={p.onCalibrate} data-testid="mt-line-set-scale">
                set a scale
              </button>{' '}
              on the drawing under it.
            </div>
          )
        ) : null}
        {line.type !== 'shape' && !line.elbowed ? (
          <>
            <button type="button" className="xb-btn" onClick={() => p.onEditPoints(line.elementId)} data-testid="mt-edit-points" title={withKey('Edit points', 'estimate.editPoints')}>
              {line.editing ? 'Editing points…' : 'Edit points'}
            </button>
            <p className="xb-mt-note">{line.editing ? 'Drag a point to move it, drag the small dots between points to add a bend, Delete removes a selected point · Esc when done' : 'Add a bend: Edit points, then drag the small dots between points.'}</p>
            <button type="button" className="xb-btn" onClick={() => p.onToggleCurve(line.elementId)} data-testid="mt-curve" title="Curved lines are measured along the curve">
              {line.curved ? 'Make sharp' : 'Make curved'}
            </button>
          </>
        ) : null}
        {measure && !measure.isMeasure ? (
          <button type="button" className="xb-btn" onClick={() => p.onMeasureLine(line.elementId)} data-testid="mt-measure-this">
            Measure this {measure.kind === 'area' ? 'shape' : 'line'}
          </button>
        ) : null}
        {line.along ? (
          <div className="xb-mt-value" data-testid="mt-along-summary">
            {line.along.count == null
              ? `${line.along.code} · no scale under this line`
              : `${line.along.code} ×${line.along.count}`}
            <small>
              {' '}
              · {trimNum(line.along.unitLength)} {line.along.unit} each
            </small>
          </div>
        ) : null}
        {p.assignOptions && p.onAssignTo && (p.assignOptions.items.length || p.assignOptions.counts?.length || p.assignOptions.presets.length) ? (
          <AssignPicker
            {...p}
            elementId={line.elementId}
            prompt={line.along ? 'Assign to another item…' : 'Assign to item…'}
            current={
              line.along
                ? (p.assignOptions.counts ?? []).find((c) => c.id === line.along!.itemId) && {
                    ...(p.assignOptions.counts ?? []).find((c) => c.id === line.along!.itemId)!,
                    unitLength: line.along.unitLength,
                    unit: line.along.unit,
                  }
                : null
            }
          />
        ) : (
          <button type="button" className="xb-btn" onClick={p.onAssign} data-testid="mt-assign" title="Count it as a run / area of a fixture type (Estimate panel)">
            Assign to item…
          </button>
        )}
      </div>
    );
  }
  if (image) {
    return (
      <div className="xb-mt-card" data-testid="mt-image-card">
        <div className="xb-mt-card-h">Drawing{image.sourceLabel ? ` · ${image.sourceLabel}` : ''}</div>
        <div className={image.scaleLabel ? 'xb-mt-value' : 'xb-mt-note'} data-testid="mt-image-scale">
          {image.scaleLabel ? `📏 ${image.scaleLabel}` : 'No scale yet'}
          {image.locked ? ' · 🔒' : ''}
        </div>
        <button type="button" className="xb-btn xb-btn-primary" onClick={p.onCalibrate} data-testid="mt-image-calibrate">
          {image.scaleLabel ? 'Recalibrate…' : 'Set scale…'}
        </button>
        {image.scaleLabel ? (
          <button type="button" className="xb-btn" onClick={() => p.onRemoveScale(image.elementId)} data-testid="mt-image-remove">
            Remove scale
          </button>
        ) : null}
      </div>
    );
  }
  if (fixture) {
    return (
      <div className="xb-mt-card" data-testid="mt-fixture-card">
        <div className="xb-mt-card-h">Fixture</div>
        <div className="xb-mt-value">{fixture.label}</div>
        {fixture.measure === 'count' ? <div className="xb-mt-note">Qty on this symbol: ×{fixture.qty}</div> : null}
        {p.scopeChoices && p.onFixtureScope ? (
          <label className="xb-mt-field">
            <span className="xb-mt-note">Scope (this fixture only)</span>
            <ScopeSelect value={fixture.scope ?? ''} onChange={(v) => p.onFixtureScope!(fixture.elementId, v)} choices={p.scopeChoices} inheritLabel="zone / type" testId="mt-fixture-scope" />
          </label>
        ) : null}
        <button type="button" className="xb-btn" onClick={p.onAssign} data-testid="mt-fixture-edit">
          Edit in Estimate panel
        </button>
      </div>
    );
  }
  return null;
}
